import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { 
  Edit, 
  Save, 
  X, 
  AlertTriangle,
  Trophy,
  DollarSign,
  Scroll,
  Loader2
} from "lucide-react";
import { motion } from "framer-motion";

interface TournamentRulesEditorProps {
  tournamentId: string | number;
  isHost: boolean;
  initialData?: {
    rules?: string;
    prizeDetails?: string;
    additionalInfo?: string;
  };
}

export function TournamentRulesEditor({ 
  tournamentId,
  isHost,
  initialData = {}
}: TournamentRulesEditorProps) {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("rules");
  const [formData, setFormData] = useState({
    rules: initialData.rules || "",
    prizeDetails: initialData.prizeDetails || "",
    additionalInfo: initialData.additionalInfo || ""
  });
  
  // Update form data when initialData changes
  useEffect(() => {
    setFormData({
      rules: initialData.rules || "",
      prizeDetails: initialData.prizeDetails || "",
      additionalInfo: initialData.additionalInfo || ""
    });
  }, [initialData]);

  // Mutation for updating tournament rules and details
  const updateRulesMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const res = await apiRequest(
        "PUT", 
        `/api/tournaments/${tournamentId}/rules`, 
        data
      );
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "Failed to update tournament details");
      }
      
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Tournament details updated successfully",
        duration: 3000,
      });
      setIsEditing(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleInputChange = (
    field: keyof typeof formData,
    value: string
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    updateRulesMutation.mutate(formData);
  };

  // Check if any content exists
  const hasContent = formData.rules || formData.prizeDetails || formData.additionalInfo;

  // Function to render placeholder when no content exists
  const renderEmptyState = () => (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      className="text-center py-8"
    >
      <div className="rounded-full bg-primary/10 p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
        <Scroll className="h-10 w-10 theme-text-primary opacity-80" />
      </div>
      <h3 className="text-xl font-medium theme-text mb-2">
        No Tournament Details Available
      </h3>
      <p className="text-sm theme-text-muted mb-6 max-w-md mx-auto">
        {isHost 
          ? "Add rules, prize information, and other relevant details for your tournament participants." 
          : "The tournament host hasn't added any details yet."}
      </p>
      {isHost && (
        <Button
          onClick={() => setIsEditing(true)}
          variant="default"
          className="mx-auto"
        >
          <Edit className="h-4 w-4 mr-2" />
          <span>Add Tournament Details</span>
        </Button>
      )}
    </motion.div>
  );

  // Function to render formatted content
  const renderContent = (content: string) => {
    if (!content) {
      return (
        <p className="text-sm italic theme-text-muted">No information provided</p>
      );
    }
    
    // Split by new lines and render paragraphs
    return content.split('\n').map((paragraph, index) => (
      <p key={index} className="mb-3 last:mb-0">
        {paragraph}
      </p>
    ));
  };

  return (
    <Card className="theme-card">
      {/* When editing */}
      {isEditing ? (
        <>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Edit Tournament Details
            </CardTitle>
            <CardDescription>
              Provide important information about your tournament for participants
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4 grid grid-cols-3">
                <TabsTrigger value="rules">Rules</TabsTrigger>
                <TabsTrigger value="prizes">Prizes</TabsTrigger>
                <TabsTrigger value="additional">Additional Info</TabsTrigger>
              </TabsList>
              
              <TabsContent value="rules">
                <div className="space-y-2">
                  <Label htmlFor="rules">Tournament Rules</Label>
                  <Textarea
                    id="rules"
                    placeholder="Enter tournament rules here..."
                    className="min-h-[200px] theme-border"
                    value={formData.rules}
                    onChange={(e) => handleInputChange("rules", e.target.value)}
                  />
                </div>
              </TabsContent>
              
              <TabsContent value="prizes">
                <div className="space-y-2">
                  <Label htmlFor="prizeDetails">Prize Details</Label>
                  <Textarea
                    id="prizeDetails"
                    placeholder="Enter prize information here..."
                    className="min-h-[200px] theme-border"
                    value={formData.prizeDetails}
                    onChange={(e) => handleInputChange("prizeDetails", e.target.value)}
                  />
                </div>
              </TabsContent>
              
              <TabsContent value="additional">
                <div className="space-y-2">
                  <Label htmlFor="additionalInfo">Additional Information</Label>
                  <Textarea
                    id="additionalInfo"
                    placeholder="Enter any additional tournament information here..."
                    className="min-h-[200px] theme-border"
                    value={formData.additionalInfo}
                    onChange={(e) => handleInputChange("additionalInfo", e.target.value)}
                  />
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button 
              variant="outline" 
              onClick={() => setIsEditing(false)} 
              disabled={updateRulesMutation.isPending}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={updateRulesMutation.isPending}
            >
              {updateRulesMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Details
                </>
              )}
            </Button>
          </CardFooter>
        </>
      ) : !hasContent ? (
        renderEmptyState()
      ) : (
        <>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2">
                <Scroll className="h-5 w-5 theme-text-primary" />
                Tournament Rules & Details
              </CardTitle>
              {isHost && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setIsEditing(true)}
                  className="h-8"
                >
                  <Edit className="h-3.5 w-3.5 mr-1.5" />
                  <span className="text-sm">Edit</span>
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <Tabs defaultValue="rules">
              <TabsList className="mb-4 grid grid-cols-3">
                <TabsTrigger value="rules" className="relative">
                  Rules
                  {formData.rules && (
                    <span className="absolute top-0 right-1 h-2 w-2 bg-primary rounded-full" />
                  )}
                </TabsTrigger>
                <TabsTrigger value="prizes" className="relative">
                  Prizes
                  {formData.prizeDetails && (
                    <span className="absolute top-0 right-1 h-2 w-2 bg-primary rounded-full" />
                  )}
                </TabsTrigger>
                <TabsTrigger value="additional" className="relative">
                  Additional
                  {formData.additionalInfo && (
                    <span className="absolute top-0 right-1 h-2 w-2 bg-primary rounded-full" />
                  )}
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="rules">
                <div className="space-y-1">
                  <h3 className="font-semibold flex items-center gap-2 mb-3">
                    <Trophy className="h-4 w-4 theme-text-primary" />
                    Tournament Rules
                  </h3>
                  <div className="text-sm theme-text">
                    {renderContent(formData.rules)}
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="prizes">
                <div className="space-y-1">
                  <h3 className="font-semibold flex items-center gap-2 mb-3">
                    <DollarSign className="h-4 w-4 theme-text-primary" />
                    Prize Information
                  </h3>
                  <div className="text-sm theme-text">
                    {renderContent(formData.prizeDetails)}
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="additional">
                <div className="space-y-1">
                  <h3 className="font-semibold flex items-center gap-2 mb-3">
                    <AlertTriangle className="h-4 w-4 theme-text-primary" />
                    Additional Information
                  </h3>
                  <div className="text-sm theme-text">
                    {renderContent(formData.additionalInfo)}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </>
      )}
    </Card>
  );
}